import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ClockClockwise,
  ArrowsClockwise,
  FileCsv,
  CaretLeft,
  CaretRight,
  X,
  Eye,
  ShieldCheck,
  Plus,
  Pencil,
  Trash,
  SignIn,
  SignOut,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ENTITIES = [
  { value: "", label: "Todas" },
  { value: "products", label: "Productos" },
  { value: "sales", label: "Ventas" },
  { value: "posts", label: "Publicaciones" },
  { value: "users", label: "Usuarios" },
  { value: "sucursales", label: "Sucursales" },
  { value: "roles", label: "Roles" },
  { value: "settings", label: "Ajustes" },
  { value: "auth", label: "Autenticación" },
];

const ACTIONS = [
  { value: "", label: "Todas" },
  { value: "create", label: "Crear" },
  { value: "update", label: "Editar" },
  { value: "delete", label: "Eliminar" },
  { value: "login", label: "Login" },
  { value: "logout", label: "Logout" },
  { value: "login_failed", label: "Login fallido" },
];

const ACTION_META = {
  create: { color: "#1B5E20", bg: "#E8F5E9", icon: Plus },
  update: { color: "#0D47A1", bg: "#E3F2FD", icon: Pencil },
  delete: { color: "#B71C1C", bg: "#FFEBEE", icon: Trash },
  login: { color: "#0D47A1", bg: "#E3F2FD", icon: SignIn },
  logout: { color: "#37474F", bg: "#ECEFF1", icon: SignOut },
  login_failed: { color: "#E65100", bg: "#FFF3E0", icon: Warning },
};

const PAGE_SIZE = 50;

export default function Auditoria() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const entityIdFilter = search.get("entity_id") || "";
  const entityIdLabel = search.get("label") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    entity: search.get("entity") || "",
    action: "",
    user_id: search.get("user_id") || "",
    date_from: "",
    date_to: "",
  });
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (filters.entity) params.entity = filters.entity;
      if (filters.action) params.action = filters.action;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (entityIdFilter) params.entity_id = entityIdFilter;
      const { data } = await api.get("/audit", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Solo administradores pueden ver la auditoría");
      } else {
        toast.error("No se pudo cargar la bitácora");
      }
    } finally {
      setLoading(false);
    }
  }, [skip, filters, entityIdFilter]);

  // Load users once for the user filter dropdown
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/users")
      .then((r) => setUsers(r.data || []))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const updateFilter = (k, v) => {
    setSkip(0);
    setFilters((f) => ({ ...f, [k]: v }));
  };

  const clearEntityIdFilter = () => {
    navigate("/auditoria", { replace: true });
  };

  const activeUser = users.find((u) => u.id === filters.user_id);

  const exportCsv = () => {
    if (!items.length) {
      toast.info("No hay registros para exportar");
      return;
    }
    const header = ["Fecha", "Usuario", "Email", "Rol", "Acción", "Entidad", "ID", "Resumen", "IP"];
    const rows = items.map((it) => [
      it.timestamp,
      it.user_name,
      it.user_email,
      it.user_role,
      it.action,
      it.entity,
      it.entity_id || "",
      (it.summary || "").replace(/"/g, '""'),
      it.ip,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${c ?? ""}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV descargado");
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  if (!isAdmin) {
    return (
      <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[800px]">
        <div className="border border-zinc-200 p-8 text-center">
          <ShieldCheck size={32} className="mx-auto mb-3 text-klein" />
          <h2 className="h-display text-xl font-bold mb-1">Solo administradores</h2>
          <p className="text-sm text-muted2">
            La bitácora del sistema solo es visible para usuarios con rol de administrador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="auditoria-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
        <div>
          <div className="overline text-klein flex items-center gap-1.5">
            <ClockClockwise size={12} weight="fill" /> Bitácora
          </div>
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
            Auditoría del sistema.
          </h1>
          <p className="text-sm text-muted2 mt-2 max-w-xl">
            Registro inmutable de todo lo que ocurre en VendeIA: altas, ediciones, eliminaciones y accesos.
            Filtra por usuario, entidad o fecha y exporta a CSV.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={load}
            className="btn-ghost"
            data-testid="audit-reload"
            data-help="Vuelve a cargar la bitácora con los filtros actuales."
          >
            <ArrowsClockwise size={16} /> Recargar
          </button>
          <button
            onClick={exportCsv}
            className="btn-ghost"
            data-testid="audit-export-csv"
            data-help="Descarga el resultado actual como archivo CSV (compatible con Excel)."
          >
            <FileCsv size={16} /> Exportar CSV
          </button>
        </div>
      </div>

      {entityIdFilter && (
        <div
          className="border border-klein/40 bg-klein/[0.05] p-3 mb-5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="audit-entity-filter"
        >
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="overline text-klein">Historial de</span>
            <span className="font-medium uppercase tracking-wide">{filters.entity || "entidad"}</span>
            {entityIdLabel && <span className="font-medium">· {entityIdLabel}</span>}
            <span className="text-muted2 font-mono text-xs">{entityIdFilter}</span>
          </div>
          <button
            type="button"
            onClick={clearEntityIdFilter}
            className="btn-ghost text-xs px-3 py-1.5"
            data-testid="audit-entity-filter-clear"
          >
            <X size={14} /> Quitar filtro
          </button>
        </div>
      )}

      {/* Filters */}
      <section className="border border-zinc-200 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="audit-filters">
        <div>
          <label className="overline block mb-1">Entidad</label>
          <select
            className="input-flat"
            value={filters.entity}
            onChange={(e) => updateFilter("entity", e.target.value)}
            data-testid="audit-filter-entity"
          >
            {ENTITIES.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-1">Acción</label>
          <select
            className="input-flat"
            value={filters.action}
            onChange={(e) => updateFilter("action", e.target.value)}
            data-testid="audit-filter-action"
          >
            {ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-1">Usuario</label>
          <select
            className="input-flat"
            value={filters.user_id}
            onChange={(e) => updateFilter("user_id", e.target.value)}
            data-testid="audit-filter-user"
            data-help="Ve toda la actividad de un usuario específico — útil para investigar incidentes o evaluar desempeño."
          >
            <option value="">— Todos —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email} ({u.role})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="overline block mb-1">Desde</label>
          <input
            type="date"
            className="input-flat"
            value={filters.date_from}
            onChange={(e) => updateFilter("date_from", e.target.value)}
            data-testid="audit-filter-from"
          />
        </div>
        <div>
          <label className="overline block mb-1">Hasta</label>
          <input
            type="date"
            className="input-flat"
            value={filters.date_to}
            onChange={(e) => updateFilter("date_to", e.target.value)}
            data-testid="audit-filter-to"
          />
        </div>
      </section>

      {filters.user_id && activeUser && (
        <div
          className="border border-amber-300 bg-amber-50 p-3 mb-5 flex items-center justify-between gap-3 flex-wrap text-sm"
          data-testid="audit-user-active"
        >
          <span>
            Mostrando actividad de <strong>{activeUser.name || activeUser.email}</strong>{" "}
            <span className="text-muted2 text-xs">({activeUser.email} · {activeUser.role})</span>
          </span>
          <button
            type="button"
            onClick={() => updateFilter("user_id", "")}
            className="btn-ghost text-xs px-3 py-1.5"
            data-testid="audit-user-clear"
          >
            <X size={14} /> Quitar filtro de usuario
          </button>
        </div>
      )}

      {/* Desktop table */}
      <section className="border border-zinc-200 hidden lg:block" data-testid="audit-table">
        <div className="grid grid-cols-12 px-4 py-3 bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-[0.2em] font-medium text-muted2">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-2">Usuario</div>
          <div className="col-span-1">Acción</div>
          <div className="col-span-2">Entidad</div>
          <div className="col-span-4">Resumen</div>
          <div className="col-span-1 text-right">Detalle</div>
        </div>
        {loading && (
          <div className="px-4 py-12 text-center text-sm text-muted2">Cargando…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted2">
            No hay registros con estos filtros.
          </div>
        )}
        {items.map((it) => {
          const meta = ACTION_META[it.action] || { color: "#475569", bg: "#F1F5F9", icon: Eye };
          const Ico = meta.icon;
          return (
            <div
              key={it.id}
              className="grid grid-cols-12 px-4 py-2.5 border-b border-zinc-100 text-sm items-center hover:bg-zinc-50"
              data-testid={`audit-row-${it.id}`}
            >
              <div className="col-span-2 text-[12px] tabular-nums">{formatDate(it.timestamp)}</div>
              <div className="col-span-2 min-w-0">
                <div className="font-medium truncate">{it.user_name || "—"}</div>
                <div className="text-[10px] text-muted2 truncate">{it.user_email}</div>
              </div>
              <div className="col-span-1">
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-medium border"
                  style={{ color: meta.color, background: meta.bg, borderColor: meta.color + "33" }}
                >
                  <Ico size={10} weight="bold" /> {it.action}
                </span>
              </div>
              <div className="col-span-2 min-w-0">
                <div className="font-medium text-xs uppercase">{it.entity}</div>
                {it.entity_id && (
                  <div className="text-[10px] text-muted2 font-mono truncate">{it.entity_id}</div>
                )}
              </div>
              <div className="col-span-4 text-[13px] truncate">{it.summary}</div>
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => setDetail(it)}
                  className="p-1.5 hover:bg-zinc-100 text-klein"
                  data-testid={`audit-detail-${it.id}`}
                  title="Ver detalle"
                >
                  <Eye size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </section>

      {/* Mobile cards */}
      <section className="lg:hidden space-y-3" data-testid="audit-cards">
        {loading && <div className="text-center text-sm text-muted2 py-8">Cargando…</div>}
        {!loading && items.length === 0 && (
          <div className="border border-zinc-200 px-4 py-12 text-center text-sm text-muted2">
            No hay registros con estos filtros.
          </div>
        )}
        {items.map((it) => {
          const meta = ACTION_META[it.action] || { color: "#475569", bg: "#F1F5F9", icon: Eye };
          const Ico = meta.icon;
          return (
            <button
              key={it.id}
              onClick={() => setDetail(it)}
              className="border border-zinc-200 p-3 w-full text-left hover:border-klein"
              data-testid={`audit-card-${it.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-medium border shrink-0"
                  style={{ color: meta.color, background: meta.bg, borderColor: meta.color + "33" }}
                >
                  <Ico size={10} weight="bold" /> {it.action} · {it.entity}
                </span>
                <span className="text-[10px] text-muted2 tabular-nums shrink-0">{formatDate(it.timestamp)}</span>
              </div>
              <div className="mt-2 text-sm font-medium">{it.summary}</div>
              <div className="text-[11px] text-muted2 mt-1">
                {it.user_name || "—"} · {it.user_email}
              </div>
            </button>
          );
        })}
      </section>

      {/* Pagination */}
      <div className="mt-5 flex items-center justify-between" data-testid="audit-pagination">
        <div className="text-xs text-muted2">
          Página {currentPage} de {totalPages} · {total} registro{total === 1 ? "" : "s"}
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            disabled={skip === 0 || loading}
            className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-50"
            data-testid="audit-prev"
          >
            <CaretLeft size={14} /> Anterior
          </button>
          <button
            onClick={() => setSkip(skip + PAGE_SIZE)}
            disabled={skip + PAGE_SIZE >= total || loading}
            className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-50"
            data-testid="audit-next"
          >
            Siguiente <CaretRight size={14} />
          </button>
        </div>
      </div>

      {detail && <DetailModal item={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function DetailModal({ item, onClose }) {
  const fmt = (v) =>
    typeof v === "string" || typeof v === "number" || typeof v === "boolean"
      ? String(v)
      : JSON.stringify(v, null, 2);
  const meta = ACTION_META[item.action] || { color: "#475569" };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="audit-detail-modal"
    >
      <div
        className="bg-white border border-zinc-200 max-w-2xl w-full max-h-[90vh] overflow-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="overline" style={{ color: meta.color }}>
              {item.action} · {item.entity}
            </div>
            <h2 className="h-display text-xl font-bold tracking-tight mt-1">{item.summary}</h2>
            <p className="text-xs text-muted2 mt-1">
              {formatDate(item.timestamp)} · {item.user_name} ({item.user_email}) · IP {item.ip || "—"}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100" data-testid="audit-detail-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs mb-4">
          <Field label="ID entidad" value={item.entity_id || "—"} mono />
          <Field label="Sucursal" value={item.sucursal_id || "—"} mono />
          <Field label="Usuario rol" value={item.user_role || "—"} />
          <Field label="User-Agent" value={(item.user_agent || "").slice(0, 60) || "—"} />
        </div>

        {item.before && (
          <div className="mb-3">
            <div className="overline mb-1">Antes</div>
            <pre className="text-[10px] bg-zinc-50 border border-zinc-200 p-3 overflow-auto max-h-48 font-mono leading-relaxed">
              {fmt(item.before)}
            </pre>
          </div>
        )}
        {item.after && (
          <div>
            <div className="overline mb-1">Después</div>
            <pre className="text-[10px] bg-zinc-50 border border-zinc-200 p-3 overflow-auto max-h-48 font-mono leading-relaxed">
              {fmt(item.after)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`text-xs ${mono ? "font-mono" : ""} truncate`} title={value}>
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
